import thousands from 'thousands';
import { get } from 'lodash';

import { getActivityFinancials, isLinkedActivity } from './financials';

/**
 * TAK-2086: the Financial Summary shown on the Documentation page and in the
 * report's Financial Result appendix. The Documentation page builds it from
 * the activity it already has (buildFinancialSummary); the Report Builder
 * gets the same shape from GET /report-builder/activity-financials. Both
 * render through financialSummaryRows, so the two tables always match.
 */

// sActivityPlanType: Grants Program
const GRANTS_PROGRAM_TYPE_ID = 4;
// sProject.statusId: Development (not yet green lit)
const DEVELOPMENT_STATUS_ID = 1;

const sumInKind = ({ partners, actual }) =>
  (partners || [])
    .filter((partner) => partner.supportType === 'IN_KIND')
    .reduce((total, partner) => {
      const amount =
        actual && partner.actualCost != null
          ? partner.actualCost
          : partner.cost;
      return total + Math.round(Number(amount || 0) * 100);
    }, 0) / 100;

export const buildFinancialSummary = (activity) => {
  const linked = isLinkedActivity(activity);
  const showActual = Number(activity.statusId) !== DEVELOPMENT_STATUS_ID;
  const fundingPartners = (activity.partners || []).filter(
    (partner) =>
      partner.supportType === 'FUNDING' &&
      !(
        linked &&
        (partner.isFromProgramLevel ||
          get(partner, 'partner.isFromProgramLevel'))
      )
  );
  return {
    linked,
    isGrant:
      linked &&
      get(activity, 'parentActivity.activityPlanTypeId') ===
        GRANTS_PROGRAM_TYPE_ID,
    showActual,
    currencySign: get(activity, 'currencyType.sign', '$'),
    counts: {
      venues: (activity.venues || []).length,
      contractors: (activity.contractors || []).length,
      partners: fundingPartners.length,
    },
    projected: getActivityFinancials({ activity }),
    actual: showActual
      ? getActivityFinancials({ activity, actual: true })
      : null,
    inKind: {
      projected: sumInKind({ partners: activity.partners }),
      actual: showActual
        ? sumInKind({ partners: activity.partners, actual: true })
        : null,
    },
  };
};

/**
 * The table rows for a summary: one per line item — expenses first, then
 * income — then the Financial Result, then the in-kind note. Expenses are
 * shown as negatives so each column adds down to the Result. Each cell is
 * { text, negative }. `kind` is 'program' (a linked activity's Program
 * Contribution / Grant, set apart at the top), 'item', 'result' or 'note'.
 */
export const financialSummaryRows = ({ summary, t }) => {
  const key = (name) => t(`activity.documentation.financialSummary.${name}`);
  const label = (singular, plural, n) => key(n === 1 ? singular : plural);
  const { counts } = summary;

  const money = (value) => {
    const cents = Math.round(Number(value || 0) * 100);
    const amount = thousands((Math.abs(cents) / 100).toFixed(2));
    return {
      text: `${cents < 0 ? '−' : ''}${summary.currencySign}${amount}`,
      negative: cents < 0,
    };
  };
  const columns = (pick) =>
    [summary.projected, ...(summary.showActual ? [summary.actual] : [])].map(
      (financials, i) => pick(financials, i === 1)
    );

  // [label, field, direction] — direction -1 shows the amount as an expense
  const items = [
    ...(summary.linked
      ? [
          [
            key(summary.isGrant ? 'program_grant' : 'program_contribution'),
            'programContribution',
            1,
          ],
        ]
      : []),
    [label('venue', 'venues', counts.venues), 'venueHire', -1],
    [label('contractor', 'contractors', counts.contractors), 'contractors', -1],
    [key('other_expenditure'), 'otherExpenditure', -1],
    [label('partner', 'partners', counts.partners), 'partnerFunding', 1],
    [key('other_income'), 'otherIncome', 1],
  ];

  return [
    ...items.map(([text, field, direction]) => ({
      key: field,
      kind: field === 'programContribution' ? 'program' : 'item',
      label: text,
      cells: columns((financials) => money(direction * financials[field])),
    })),
    {
      key: 'result',
      kind: 'result',
      label: key('financial_result'),
      cells: columns((financials) => money(financials.result)),
    },
    {
      key: 'inKind',
      kind: 'note',
      label: key('in_kind_support'),
      cells: columns((financials, isActual) => ({
        ...money(isActual ? summary.inKind.actual : summary.inKind.projected),
        negative: false,
      })),
    },
  ];
};

export const financialSummaryHeadings = ({ summary, t }) => [
  t('activity.documentation.financialSummary.projected'),
  ...(summary.showActual
    ? [t('activity.documentation.financialSummary.actual')]
    : []),
];
